/**
 * Cart drawer upsell quick view.
 *
 * The plus button on an upsell card fetches sections/cart-upsell-quick-view
 * through the Section Rendering API against the product's own URL — that is what
 * puts `product` in scope for the section — and slides the result in over the
 * drawer. Add to cart goes through liquidAjaxCart so the drawer re-renders
 * itself, exactly as the carousel's own add path used to.
 *
 * Everything is delegated on document: the panel markup is injected, so nothing
 * can be bound at load time, and the drawer replaces its own body on every cart
 * mutation.
 */

const SECTION_ID = 'cart-upsell-quick-view';

function panel() {
  return document.querySelector('[data-cart-quick-view]');
}

/**
 * Mirrors snippets/price-format.liquid, which renders the panel's first price:
 * US-style separators, and cents only when there are any ($12, $12.50).
 *
 * @param {number|string} cents - Variant price in cents.
 * @returns {string}
 */
function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'USD';
  const fractionDigits = Number(cents) % 100 === 0 ? 0 : 2;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(Number(cents) / 100);
  } catch {
    return (Number(cents) / 100).toFixed(2).replace(/\.00$/, '') + ' ' + currency;
  }
}

function setMedia(root, variant) {
  const img = root.querySelector('[data-cart-quick-view-image] img');
  const src = variant?.featured_image?.src;
  if (!img || !src) return;

  const base = src + (src.includes('?') ? '&' : '?');
  img.srcset = [400, 600, 1000].map((width) => `${base}width=${width} ${width}w`).join(', ');
  img.src = `${base}width=1000`;
}

function dropStaleSizeGuides() {
  for (const element of document.querySelectorAll(`[id^="size-guide-drawer-${SECTION_ID}"]`)) {
    element.remove();
  }
}

let openHandle = null;
let latestRequest = 0;
let opener = null;

function setBackdropInert(inert) {
  const parent = panel()?.parentElement;
  if (!parent) return;
  for (const child of parent.children) {
    if (child !== panel()) child.inert = inert;
  }
}

function close() {
  const root = panel();
  if (!root) return;
  openHandle = null;
  latestRequest += 1;
  root.removeAttribute('data-open');
  root.removeAttribute('data-loading');
  setBackdropInert(false);
  // Send focus back where it came from, so keyboard users are not dumped at
  // the top of the document after stepping back to the cart.
  if (opener?.isConnected) opener.focus({ preventScroll: true });
  opener = null;
  dropStaleSizeGuides();
  // Keep the markup until the slide-out finishes, then drop it so the next
  // open always starts from a clean, correctly-scrolled panel.
  setTimeout(() => {
    if (!root.hasAttribute('data-open')) root.replaceChildren();
  }, 300);
}

/**
 * Open the panel on the metal the card was pictured in.
 *
 * The card decides that metal (a mixed cart spreads its metals across the
 * carousel), so it is passed in rather than worked out again here — the panel
 * must never open on a different metal than the card that opened it. Handing
 * syncSelection the option index makes it reconcile the other options, so a
 * size that does not exist in the new metal moves to one that does.
 *
 * @param {HTMLElement} root - The injected quick-view panel.
 * @param {string} [metal] - Option value the card was pictured in, from its button.
 * @returns {void}
 */
function applyMetal(root, metal) {
  const input = metal
    ? Array.from(root.querySelectorAll('[data-cart-quick-view-option]')).find((i) => i.value === metal)
    : null;

  if (!input || input.checked) {
    syncSelection(root);
    return;
  }

  input.checked = true;
  syncSelection(root, Number(input.dataset.cartQuickViewOption));
}

async function open(handle, metal) {
  const root = panel();
  if (!root || !handle) return;

  // Re-triggering the product the panel already shows — or is still loading —
  // must be a no-op. Restarting tore the content down and rebuilt it mid-slide,
  // which read as the panel closing and then opening again.
  if (openHandle === handle && root.hasAttribute('data-open')) return;

  openHandle = handle;
  const request = (latestRequest += 1);

  root.setAttribute('data-open', '');
  root.setAttribute('data-loading', '');
  // Contain focus before the fetch, so keyboard users cannot tab behind the
  // overlay while the product loads.
  setBackdropInert(true);
  root.focus({ preventScroll: true });

  try {
    const base = window.Shopify?.routes?.root || '/';
    const url = new URL(`${base}products/${handle}`, window.location.origin);
    url.searchParams.set('section_id', SECTION_ID);
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Quick view failed: ${res.status}`);
    const html = await res.text();
    if (request !== latestRequest) return;
    // Before the incoming markup connects its own drawer and hoists it too.
    dropStaleSizeGuides();
    root.innerHTML = html;
    root.scrollTop = 0;
    applyMetal(root, metal);
    // Focus lands on Back: the first control in the panel, and the way out.
    root.querySelector('[data-cart-quick-view-close]')?.focus({ preventScroll: true });
  } catch (error) {
    // A superseded request must not close the panel a newer one just opened.
    if (request !== latestRequest) return;
    console.error(error);
    close();
  } finally {
    if (request === latestRequest) root.removeAttribute('data-loading');
  }
}

/* ------------------------------------------------------------- variant logic */

/* Mirrors lp-pdp-no-customizer-product-form.liquid so both pickers agree. */
function readVariants(root) {
  const el = root.querySelector('[data-cart-quick-view-variants]');
  if (!el) return [];
  try {
    return JSON.parse(el.textContent);
  } catch {
    return [];
  }
}

function selectedValues(root) {
  return Array.from(root.querySelectorAll('[data-cart-quick-view-option]'))
    .filter((input) => input.checked)
    .sort((a, b) => a.dataset.cartQuickViewOption - b.dataset.cartQuickViewOption)
    .map((input) => input.value);
}

function matchVariant(variants, values) {
  if (!values.length) return variants[0] || null;
  return variants.find((v) => v.options.every((opt, i) => opt === values[i])) || null;
}

/**
 * Hide values that cannot form a real variant with the other selected values,
 * steer the other groups back to a real variant when the user's pick breaks the
 * combination, then refresh the price, the selected-value labels and the button.
 */
function syncSelection(root, changedIndex) {
  const variants = readVariants(root);
  if (!variants.length) return;

  const inputs = Array.from(root.querySelectorAll('[data-cart-quick-view-option]'));

  // Reconcile: keep what the user just changed, move the rest to a real variant.
  if (changedIndex !== undefined && !matchVariant(variants, selectedValues(root))) {
    const changed = selectedValues(root)[changedIndex];
    const candidate = variants.find((v) => v.options[changedIndex] === changed);
    if (candidate) {
      for (const input of inputs) {
        const index = Number(input.dataset.cartQuickViewOption);
        if (index !== changedIndex && input.value === candidate.options[index]) input.checked = true;
      }
    }
  }

  const values = selectedValues(root);
  for (const input of inputs) {
    const index = Number(input.dataset.cartQuickViewOption);
    const exists = variants.some(
      (v) =>
        v.options[index] === input.value &&
        v.options.every((opt, i) => i === index || opt === values[i])
    );
    input.disabled = !exists;
    const label = input.closest('.hy-qv__value');
    if (label) label.hidden = !exists;
  }

  const variant = matchVariant(variants, selectedValues(root));

  for (const label of root.querySelectorAll('[data-cart-quick-view-selected]')) {
    label.textContent = selectedValues(root)[Number(label.dataset.cartQuickViewSelected)] || '';
  }

  const price = root.querySelector('[data-cart-quick-view-price]');
  if (price && variant) price.textContent = formatMoney(variant.price);
  // During a sale the price sits inside <sale-pricing> (component-sale-pricing.liquid). There is no
  // product form here for it to read the variant from, so it is told directly.
  if (variant) root.querySelector('sale-pricing')?.sync?.(variant.id);

  setMedia(root, variant);

  const add = root.querySelector('[data-cart-quick-view-add]');
  const label = root.querySelector('[data-cart-quick-view-add-label]');
  if (!add || !label) return;
  // The section renders the localised Add to cart string; remember it before
  // any state message overwrites it.
  if (!add.dataset.defaultLabel) add.dataset.defaultLabel = label.textContent.trim();

  const purchasable = Boolean(variant && variant.available);
  add.disabled = !purchasable;
  add.dataset.variantId = variant ? variant.id : '';
  // The state strings are stamped on the button by the section, so they stay
  // translatable — the button's accessible name is this label.
  label.textContent = purchasable
    ? add.dataset.defaultLabel
    : variant
      ? add.dataset.labelSoldOut || add.dataset.defaultLabel
      : add.dataset.labelUnavailable || add.dataset.defaultLabel;
}

/* ------------------------------------------------------------- add to cart */

function addToCart(root) {
  const add = root.querySelector('[data-cart-quick-view-add]');
  const id = Number(add?.dataset.variantId);
  const quantity = Math.max(1, Number(root.querySelector('[data-cart-quick-view-qty-input]')?.value) || 1);
  if (!id || add.disabled || typeof window.liquidAjaxCart?.add !== 'function') return;

  const label = root.querySelector('[data-cart-quick-view-add-label]');
  const previous = label.textContent;
  add.disabled = true;
  label.textContent = add.dataset.labelAdding || previous;

  window.liquidAjaxCart.add(
    { items: [{ id, quantity }] },
    {
      lastCallback: (state) => {
        if (state?.responseData?.ok) {
          close();
        } else {
          add.disabled = false;
          label.textContent = previous;
        }
      },
    }
  );
}

/* ---------------------------------------------------------------- listeners */

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-cart-quick-view-open]');
  if (trigger) {
    opener = trigger;
    open(trigger.dataset.productHandle, trigger.dataset.cartMetal);
    return;
  }

  const root = event.target.closest('[data-cart-quick-view-root]');
  if (!root) return;

  if (event.target.closest('[data-cart-quick-view-close]')) {
    close();
    return;
  }

  const step = event.target.closest('[data-cart-quick-view-qty]');
  if (step) {
    const input = root.querySelector('[data-cart-quick-view-qty-input]');
    input.value = Math.max(1, (Number(input.value) || 1) + Number(step.dataset.cartQuickViewQty));
    return;
  }

  if (event.target.closest('[data-cart-quick-view-add]')) addToCart(root);
});

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-cart-quick-view-option]');
  if (input) syncSelection(input.closest('[data-cart-quick-view-root]'), Number(input.dataset.cartQuickViewOption));
});

/* Escape steps back to the cart rather than closing the whole drawer. */
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !panel()?.hasAttribute('data-open')) return;
  event.stopPropagation();
  close();
});

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/* Tab and Shift+Tab cycle inside the open panel. The drawer's own trap cannot
   do it: its first and last controls sit inert behind the panel, so focus
   would slip out to the page. */
document.addEventListener('keydown', (event) => {
  const root = panel();
  if (event.key !== 'Tab' || !root?.hasAttribute('data-open')) return;

  const focusable = Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  // Still loading: nothing to move to, so keep focus on the panel itself.
  if (!first) {
    root.focus({ preventScroll: true });
    event.preventDefault();
    return;
  }

  if (event.shiftKey && (document.activeElement === first || document.activeElement === root)) {
    event.preventDefault();
    last.focus();
    return;
  }

  if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
});

/* Whichever path closes the drawer — the overlay, the close button, a product
   link, Alpine state set elsewhere — must drop the panel too, or it would still
   be sitting over the cart the next time the drawer opens. Watching the class
   the drawer toggles catches all of them without knowing about any. */
const drawer = document.getElementById('cart-drawer');
if (drawer) {
  new MutationObserver(() => {
    if (!drawer.classList.contains('cart-open')) close();
  }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
}
